import { useEffect, useRef, useState } from 'react';
import type { Automation } from '@epasu/core';
import { Plus, X } from 'lucide-react';
import { crm } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface Props {
  /** null = neue Regel */
  automation: Automation | null;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Auslöser, die es gibt. Die Beschreibung steht bewusst dabei — „deal.stage_changed“
 * sagt niemandem etwas, der die Regel nicht selbst geschrieben hat.
 */
const TRIGGER = [
  {
    key: 'deal.stage_changed',
    label: 'Ein Deal wechselt die Stufe',
    hinweis: 'Läuft bei jedem Verschieben im Kanban.',
  },
  {
    key: 'email.received',
    label: 'Eine Kundenmail kommt an',
    hinweis: 'Nur bei zugeordneter Post, nicht bei Werbung.',
  },
  {
    key: 'order.status_changed',
    label: 'Ein Auftrag wechselt den Status',
    hinweis: 'Etwa von „in Arbeit“ auf „geliefert“.',
  },
] as const;

/** Bedingungen, die sich sinnvoll setzen lassen, je Auslöser. */
const BEDINGUNGEN: Record<string, { key: string; value: unknown; label: string }[]> = {
  'deal.stage_changed': [
    { key: 'isWon', value: true, label: 'nur wenn gewonnen' },
    { key: 'isLost', value: true, label: 'nur wenn verloren' },
  ],
  'order.status_changed': [
    { key: 'orderStatus', value: 'in_arbeit', label: 'nur bei „In Arbeit“' },
    { key: 'orderStatus', value: 'geliefert', label: 'nur bei „Geliefert“' },
    { key: 'orderStatus', value: 'abgerechnet', label: 'nur bei „Abgerechnet“' },
  ],
  'email.received': [],
};

type ActionType = 'create_task' | 'create_order' | 'suppress_email' | 'log';

interface ActionRow {
  type: ActionType;
  title: string;
  dueInDays: string;
  tasks: string[];
  summary: string;
}

const leereAktion = (): ActionRow => ({
  type: 'create_task',
  title: '',
  dueInDays: '',
  tasks: [],
  summary: '',
});

export function AutomationDialog({ automation, onClose, onSaved }: Props) {
  const [name, setName] = useState(automation?.name ?? '');
  const [trigger, setTrigger] = useState(automation?.trigger ?? 'deal.stage_changed');
  const [bedingung, setBedingung] = useState(() => bedingungKey(automation));
  const [actions, setActions] = useState<ActionRow[]>(() => ladeAktionen(automation));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const setAction = (index: number, patch: Partial<ActionRow>) =>
    setActions((rows) => rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Bitte einen Namen angeben.');
      return;
    }
    if (actions.length === 0) {
      setError('Eine Regel ohne Aktion tut nichts.');
      return;
    }

    const moegliche = BEDINGUNGEN[trigger] ?? [];
    const gewaehlt = moegliche.find((b) => `${b.key}:${String(b.value)}` === bedingung);
    const conditions = gewaehlt ? { [gewaehlt.key]: gewaehlt.value } : {};

    const payload = {
      name: name.trim(),
      trigger,
      conditions,
      actions: actions.map((a) => bauAktion(a)),
    };

    setBusy(true);
    setError(null);
    try {
      if (automation) await crm.automations.update(automation.id, payload);
      else await crm.automations.create(payload);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen');
      setBusy(false);
    }
  };

  const moegliche = BEDINGUNGEN[trigger] ?? [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={automation ? 'Regel bearbeiten' : 'Neue Regel'}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <form
        onSubmit={(e) => void submit(e)}
        className="my-8 w-full max-w-2xl rounded-lg border bg-background p-6 shadow-lg"
      >
        <h2 className="text-lg font-semibold">
          {automation ? 'Regel bearbeiten' : 'Neue Regel'}
        </h2>

        <div className="mt-4 space-y-4">
          <Field label="Name">
            <Input
              ref={nameRef}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Gewonnen → Auftrag anlegen"
            />
          </Field>

          <Field label="Wenn">
            <select
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={trigger}
              onChange={(e) => {
                setTrigger(e.target.value);
                setBedingung('');
              }}
            >
              {TRIGGER.map((t) => (
                <option key={t.key} value={t.key}>
                  {t.label}
                </option>
              ))}
            </select>
            <span className="text-xs text-muted-foreground">
              {TRIGGER.find((t) => t.key === trigger)?.hinweis}
            </span>
          </Field>

          {moegliche.length > 0 && (
            <Field label="Einschränkung">
              <select
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={bedingung}
                onChange={(e) => setBedingung(e.target.value)}
              >
                <option value="">— immer —</option>
                {moegliche.map((b) => (
                  <option key={`${b.key}:${String(b.value)}`} value={`${b.key}:${String(b.value)}`}>
                    {b.label}
                  </option>
                ))}
              </select>
            </Field>
          )}
        </div>

        <fieldset className="mt-5 space-y-3 rounded-md border p-4">
          <legend className="px-1 text-xs font-medium text-muted-foreground">Dann</legend>

          {actions.map((a, index) => (
            <div key={index} className="space-y-2 rounded-md border p-3">
              <div className="flex gap-2">
                <select
                  className="h-10 flex-1 rounded-md border border-input bg-background px-3 text-sm"
                  value={a.type}
                  onChange={(e) => setAction(index, { type: e.target.value as ActionType })}
                >
                  <option value="create_task">Aufgabe anlegen</option>
                  <option value="create_order">Auftrag anlegen</option>
                  <option value="suppress_email">Kontakt abmelden</option>
                  <option value="log">Nur protokollieren</option>
                </select>
                <button
                  type="button"
                  aria-label="Aktion entfernen"
                  className="px-1 text-muted-foreground hover:text-destructive"
                  onClick={() => setActions((rows) => rows.filter((_, i) => i !== index))}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {(a.type === 'create_task' || a.type === 'create_order') && (
                <div className="grid gap-2 sm:grid-cols-[1fr_8rem]">
                  <Input
                    placeholder={
                      a.type === 'create_task' ? 'Titel der Aufgabe' : 'Titel des Auftrags'
                    }
                    value={a.title}
                    onChange={(e) => setAction(index, { title: e.target.value })}
                  />
                  <Input
                    inputMode="numeric"
                    placeholder="in X Tagen"
                    value={a.dueInDays}
                    onChange={(e) => setAction(index, { dueInDays: e.target.value })}
                  />
                </div>
              )}

              {a.type === 'create_order' && (
                <ChecklistEditor
                  tasks={a.tasks}
                  onChange={(tasks) => setAction(index, { tasks })}
                />
              )}

              {a.type === 'log' && (
                <Input
                  placeholder="Text fürs Protokoll"
                  value={a.summary}
                  onChange={(e) => setAction(index, { summary: e.target.value })}
                />
              )}

              {a.type === 'suppress_email' && (
                <p className="text-xs text-muted-foreground">
                  Setzt den Kontakt auf die Sperrliste. Danach geht keine Post mehr
                  an ihn raus — das lässt sich nur von Hand zurücknehmen.
                </p>
              )}
            </div>
          ))}

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setActions((rows) => [...rows, leereAktion()])}
          >
            <Plus className="mr-1.5 h-4 w-4" />
            Aktion
          </Button>
        </fieldset>

        {error && (
          <p className="mt-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </p>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button type="submit" disabled={busy}>
            {busy ? 'Speichere …' : 'Speichern'}
          </Button>
        </div>
      </form>
    </div>
  );
}

function ChecklistEditor({
  tasks,
  onChange,
}: {
  tasks: string[];
  onChange: (tasks: string[]) => void;
}) {
  const [neu, setNeu] = useState('');

  return (
    <div className="space-y-1.5">
      <p className="text-xs text-muted-foreground">
        Aufgaben, die mit dem Auftrag entstehen
      </p>
      {tasks.map((t, i) => (
        <div key={i} className="flex items-center gap-2 text-sm">
          <span className="flex-1">{t}</span>
          <button
            type="button"
            aria-label={`${t} entfernen`}
            className="text-muted-foreground hover:text-destructive"
            onClick={() => onChange(tasks.filter((_, index) => index !== i))}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}
      <div className="flex gap-2">
        <Input
          placeholder="Ausweise produzieren"
          value={neu}
          onChange={(e) => setNeu(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            e.preventDefault();
            if (!neu.trim()) return;
            onChange([...tasks, neu.trim()]);
            setNeu('');
          }}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            if (!neu.trim()) return;
            onChange([...tasks, neu.trim()]);
            setNeu('');
          }}
        >
          Hinzufügen
        </Button>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Umwandlung zwischen Formular und gespeicherter Regel                      */
/* -------------------------------------------------------------------------- */

function bedingungKey(automation: Automation | null): string {
  const c = automation?.conditions;
  if (!c) return '';
  const [key, value] = Object.entries(c)[0] ?? [];
  return key ? `${key}:${String(value)}` : '';
}

function ladeAktionen(automation: Automation | null): ActionRow[] {
  const roh = (automation?.actions ?? []) as Record<string, unknown>[];
  if (roh.length === 0) return [leereAktion()];

  return roh.map((a) => {
    const params = (a.params ?? {}) as Record<string, unknown>;
    return {
      type: (a.type as ActionType) ?? 'log',
      title: typeof params.title === 'string' ? params.title : '',
      dueInDays: typeof params.dueInDays === 'number' ? String(params.dueInDays) : '',
      tasks: Array.isArray(params.tasks)
        ? (params.tasks as unknown[]).filter((t): t is string => typeof t === 'string')
        : [],
      summary: typeof params.summary === 'string' ? params.summary : '',
    };
  });
}

function bauAktion(a: ActionRow): Record<string, unknown> {
  const params: Record<string, unknown> = {};
  if (a.title.trim()) params.title = a.title.trim();

  const tage = Number(a.dueInDays);
  if (a.dueInDays.trim() && Number.isFinite(tage)) params.dueInDays = tage;

  if (a.type === 'create_order' && a.tasks.length) params.tasks = a.tasks;
  if (a.type === 'log' && a.summary.trim()) params.summary = a.summary.trim();

  return Object.keys(params).length ? { type: a.type, params } : { type: a.type };
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
